<!--
  Shared bulk Excel upload for financial close.
  Path: modules/financialclose/components/BulkUpload/
  Call sites: TasklistPageHeader (tasklist-upload), TrialBalance (trial-balance),
  TasklistDrawers (tasklist-edit), InformationRequests (information-request).
  Pass test-id-prefix so shell elements use `${prefix}__${suffix}`.
-->
<template>
  <div :data-testid="resolvedTestIds.root || undefined">
    <v-btn
      v-if="showTrigger"
      color="secondaryBtn"
      :data-testid="resolvedTestIds.openButton"
      @click="openBulkUpload"
    >
      <img src="/img/icons/fbc-icons/ImportTask.svg" alt="Bulk Upload" class="pr-2">
      <span>{{ buttonLabel }}</span>
    </v-btn>

    <component
      :is="chromeComponent"
      v-if="showContainer"
      v-bind="chromeBindings"
      @input="setOpen"
    >
      <template #body>
        <BulkUploadFrame
          ref="frame"
          v-bind="frameProps"
          @download="onDownload"
          @reupload="onReupload"
          @files-change="onFilesChange"
          @reupload-file="onReuploadFileSelected"
          @update:showErrorsOnly="onShowErrorsOnly"
        >
          <template #preview>
            <slot name="preview">
                <!-- Bulk tasklist-edit preview (externalMode bulk-edit) -->
                <div
                  v-if="isBulkEditMode"
                  class="bulk-import-dialog__legacy-table"
                  :data-testid="resolvedTestIds.previewTable"
                >
                  <v-simple-table :data-testid="resolvedTestIds.previewSimpleTable">
                    <template v-slot:default>
                      <thead class="tableHeaderBg">
                        <tr>
                          <th
                            v-for="header in tableHeaders"
                            :key="header.value"
                            class="tableHeaderBg"
                          >
                            {{ header.text }}
                          </th>
                        </tr>
                      </thead>
                      <v-slide-y-transition group tag="tbody">
                        <tr
                          v-for="(row, index) in bulkEditFilteredData"
                          :key="'row-' + index"
                          class="bulk-import-dialog__row"
                          :data-testid="resolvedTestIds.previewRow ? `${resolvedTestIds.previewRow}--${index}` : undefined"
                        >
                          <td
                            v-for="header in tableHeaders"
                            :key="'col-' + header.value + '-' + index"
                            class="px-4 single-line"
                            :class="bulkEditPreviewCellClass(index, header)"
                          >
                            <v-tooltip
                              v-if="bulkEditCellError(index, header.value)"
                              bottom
                              content-class="tooltip-bottom"
                            >
                              <template #activator="{ on, attrs }">
                                <div
                                  v-bind="attrs"
                                  v-on="on"
                                  class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
                                >
                                  <span class="bulk-import-dialog__cell--truncate">
                                    {{ bulkEditCellError(index, header.value) }}
                                  </span>
                                </div>
                              </template>
                              <span>{{ bulkEditCellError(index, header.value) }}</span>
                            </v-tooltip>

                            <OverflowList
                              v-else-if="isBulkEditOverflowColumn(header.value)"
                              data-row-nav-ignore
                              :items="bulkEditOverflowItems(row, header.value)"
                              :data-testid="resolvedTestIds.previewTable ? `${resolvedTestIds.previewTable}-overflow-${header.value}--${index}` : undefined"
                            />

                            <div v-else-if="header.value === 'task_type'">
                              <span v-if="row[header.value] === legacyTaskTypeLabelChecklistName">
                                {{ legacyTaskTypeLabelChecklist }} Task
                              </span>
                              <span v-else>{{ row[header.value] }}</span>
                            </div>

                            <span
                              v-else
                              :class="header.value === 'description' ? 'bulk-import-dialog__cell--truncate' : ''"
                            >
                              {{ formatPreviewCellValue(header.value, row[header.value]) }}
                            </span>
                          </td>
                        </tr>
                      </v-slide-y-transition>
                    </template>
                  </v-simple-table>
                </div>

                <!-- Information-request preview (externalMode information-request) -->
                <v-simple-table
                  v-else-if="isInformationRequestMode"
                  dense
                  class="radius-small border-on simple-table bulk-upload-modal__table"
                  :data-testid="resolvedTestIds.previewTable"
                >
                  <template v-slot:default>
                    <thead class="grayBackground">
                      <th class="text-left pa-2 border-right-data grayBackground">Errors</th>
                      <th
                        v-for="(header, index) in requestHeaders"
                        :key="index"
                        class="text-left pa-2 grayBackground"
                        :class="index < requestHeaders.length - 1 ? 'border-right-data' : ''"
                      >
                        {{ header }}
                      </th>
                    </thead>
                    <tbody>
                      <tr v-for="(row, rowIndex) in filteredRequestUploadData" :key="rowIndex">
                        <td class="bulk-upload-modal__row-error">
                          <div v-if="row.data[0].errors && row.data[0].errors.length > 0">
                            {{ row.data[0].errors }}
                          </div>
                          <span v-else class="grey--text">No errors</span>
                        </td>
                        <td>
                          <OverflowList
                            data-row-nav-ignore
                            :items="previewList((row.data[0] || {}).ref)"
                          />
                        </td>
                        <td>{{ (row.data[0] || {}).category || 'Others' }}</td>
                        <td>{{ (row.data[0] || {}).description }}</td>
                        <td>{{ (row.data[0] || {}).info_owner_name }}</td>
                        <td>{{ (row.data[0] || {}).info_owner_email }}</td>
                        <td>{{ formatRequestType(row.data[0]) }}</td>
                        <td
                          :class="requestFieldError(row, 'uploadlink') ? 'error--text' : ''"
                          :data-testid="resolvedTestIds.previewTable ? `${resolvedTestIds.previewTable}-cell-uploadlink` : undefined"
                        >
                          <v-tooltip
                            v-if="requestFieldError(row, 'uploadlink')"
                            bottom
                            content-class="tooltip-bottom"
                          >
                            <template #activator="{ on, attrs }">
                              <span
                                v-bind="attrs"
                                v-on="on"
                                class="error--text"
                              >
                                {{ requestFieldError(row, 'uploadlink') }}
                              </span>
                            </template>
                            <span>{{ requestFieldError(row, 'uploadlink') }}</span>
                          </v-tooltip>
                          <span v-else>{{ (row.data[0] || {}).uploadlink }}</span>
                        </td>
                        <td>{{ (row.data[0] || {}).priority || 'Medium' }}</td>
                        <td>{{ (row.data[0] || {}).duedate }}</td>
                        <td>
                          <OverflowList
                            data-row-nav-ignore
                            :items="previewList((row.data[0] || {}).location)"
                          />
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>

                <!-- Checklist preview table -->
                <div
                  v-else-if="uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST"
                  class="bulk-import-dialog__legacy-table"
                  :data-testid="resolvedTestIds.previewTable"
                >
                  <v-simple-table :data-testid="resolvedTestIds.previewSimpleTable">
                    <template v-slot:default>
                      <thead class="tableHeaderBg">
                        <tr>
                          <th
                            v-for="header in previewTableHeaders"
                            :key="header.value"
                            class="text-center tableHeaderBg"
                          >
                            {{ header.text }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(row, index) in filteredData"
                          :key="'checklist-preview-' + index"
                          class="bulk-import-dialog__row"
                          :data-testid="resolvedTestIds.previewRow ? `${resolvedTestIds.previewRow}--${index}` : undefined"
                        >
                          <td
                            v-for="header in previewTableHeaders"
                            :key="index + '-' + header.value"
                            class="px-4 single-line"
                            :class="checklistCellClass(index, header)"
                            :data-testid="resolvedTestIds.previewTable ? `${resolvedTestIds.previewTable}-cell-${header.value}` : undefined"
                          >
                            <v-tooltip
                              v-if="checklistCellError(index, header.value)"
                              bottom
                              content-class="tooltip-bottom"
                            >
                              <template #activator="{ on, attrs }">
                                <div
                                  v-bind="attrs"
                                  v-on="on"
                                  class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
                                >
                                  <span class="bulk-import-dialog__cell--truncate">
                                    {{ checklistCellError(index, header.value) }}
                                  </span>
                                </div>
                              </template>
                              <span>{{ checklistCellError(index, header.value) }}</span>
                            </v-tooltip>

                            <OverflowList
                              v-else-if="isChecklistOverflowColumn(header.value)"
                              data-row-nav-ignore
                              :items="checklistOverflowItems(row, header.value)"
                              :data-testid="resolvedTestIds.previewTable ? `${resolvedTestIds.previewTable}-overflow-${header.value}--${index}` : undefined"
                            />

                            <div v-else-if="header.value === 'task_type'">
                              <span v-if="row[header.value] === LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME">
                                {{ LEGACY_TASK_TYPE_LABEL_CHECKLIST }} Task
                              </span>
                              <span v-else>{{ row[header.value] }}</span>
                            </div>

                            <span
                              v-else
                              :class="header.value === 'description' ? 'bulk-import-dialog__cell--truncate' : ''"
                            >
                              {{ formatPreviewCellValue(header.value, row[header.value]) }}
                            </span>
                          </td>
                        </tr>
                      </tbody>
                    </template>
                  </v-simple-table>
                </div>

                <!-- Trial balance preview table -->
                <div
                  v-else-if="uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE"
                  class="bulk-import-dialog__legacy-table"
                  :data-testid="resolvedTestIds.previewTable"
                >
                  <v-simple-table
                    fixed-header
                    class="simple-table border-on radius-small"
                    :data-testid="resolvedTestIds.previewSimpleTable"
                  >
                    <template>
                      <thead class="tableHeaderBg">
                        <tr>
                          <th class="pa-1 tableHeaderBg text-center">S No.</th>
                          <th
                            v-for="header in computedHeaders"
                            :key="header.value"
                            class="pa-1 tableHeaderBg text-center"
                          >
                            {{ header.text }}
                          </th>
                        </tr>
                      </thead>
                      <v-slide-y-transition group tag="tbody">
                        <tr v-for="(v, k) in filteredData" :key="k">
                          <td class="text-center">{{ k + 1 }}</td>
                          <td
                            v-for="header in computedHeaders"
                            :key="`cell-${header.value}`"
                            :class="[
                              header.align ? `text-${header.align}` : 'text-left',
                              trialBalanceCellClass(k, header),
                            ]"
                          >
                            <v-tooltip
                              v-if="getTrialBalanceCellError(k, header)"
                              bottom
                              content-class="tooltip-bottom"
                            >
                              <template #activator="{ on, attrs }">
                                <div
                                  v-bind="attrs"
                                  v-on="on"
                                  class="bulk-import-dialog__cell-inner bulk-import-dialog__cell-inner--error error--text"
                                >
                                  <span class="bulk-import-dialog__cell--truncate">
                                    {{ getTrialBalanceCellError(k, header) }}
                                  </span>
                                </div>
                              </template>
                              <span>{{ getTrialBalanceCellError(k, header) }}</span>
                            </v-tooltip>
                            <span
                              v-else
                              :class="header.value === 'location' ? 'caccounting--text pa-1 mr-1 radius-mini' : ''"
                            >
                              {{ formatCellValue(getCellValue(v, header.value), header.format) }}
                            </span>
                          </td>
                        </tr>
                      </v-slide-y-transition>
                    </template>
                  </v-simple-table>
                </div>
            </slot>
          </template>
        </BulkUploadFrame>
      </template>
      <template #actions>
        <v-spacer />
        <v-btn
          v-bind="cancelButtonProps"
          :data-testid="resolvedTestIds.cancel"
          @click="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          v-bind="uploadButtonProps"
          :disabled="!resolvedCanSubmit"
          :loading="resolvedSubmitLoading"
          :data-testid="resolvedTestIds.upload"
          @click="onUpload"
        >
          Upload
        </v-btn>
      </template>
    </component>
  </div>
</template>

<script>
/**
 * Shared bulk Excel upload for financial close (replaces BulkUploadChecklistV2).
 *
 * Used by:
 * - TasklistPageHeader.vue — "Import Tasks" checklist upload (managed mode,
 *   uploadType checklist). Owns parse / validate / upload.
 * - TrialBalance.vue — trial balance upload (hideButton; opened via ref
 *   openBulkUpload()). Managed mode.
 * - TasklistDrawers.vue — external drawer, externalMode bulk-edit; parent owns
 *   parse/upload; this component renders the preview table.
 * - InformationRequests.vue — external dialog, externalMode information-request;
 *   parent owns parse/upload; this component renders the preview table.
 *
 * Modes:
 * - Managed (default): this component parses, validates, and uploads; renders
 *   its own checklist / trial-balance preview tables.
 * - External: parent drives parse/upload. Preview is built-in for
 *   externalMode bulk-edit / information-request, or via #preview slot.
 *
 * Related pieces in this folder:
 * - BulkUploadFrame.vue — shared shell (download template, drop zone,
 *   validating state, error footer).
 * - BulkUploadDialog.vue — centered dialog chrome only (information requests).
 * - BulkUpload.scss — shared styles (also imported by hosts that need the
 *   preview table look).
 */
import { OverflowList } from '@/modules/financialclose/components';
import BulkUploadDialog from './BulkUploadDialog.vue';
import BulkUploadFrame from './BulkUploadFrame.vue';
import { drawerWidths } from '../../../../styles/design-tokens.js';
import { parseCommaSeparatedField, formatDateToDMY, parseDueDateValue, getCustomColumns } from '../../utils/utils';
import {
  BULK_CHECKLIST_UPLOAD_TYPE,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
  LEGACY_TASK_TYPE_LABEL_CHECKLIST,
  SIGN_OFF,
  AUTO_APPROVE,
  BULK_EDIT_OVERFLOW_COLUMNS,
  coerceEvidenceRequired,
  isEvidenceRequiredEnabled,
} from '../../utils/constants';
import { CHECKLIST_VALIDATION_MESSAGES, TRIAL_BALANCE_VALIDATION_MESSAGES, GENERAL_ERROR_MESSAGES, HEADER_ERROR_MESSAGES, canonicalizeBulkTaskHeader, findDuplicateValues, duplicateInRoleMessage } from '../../utils/bulkUploadValidationMessages';
import {
  getRowFieldErrorMessage,
  mergeCycleTaskRowErrors,
  parseCycleTaskValidateResult,
  pickNonRowFieldErrorMessages,
  pickUnmappedRowFieldErrorMessages,
} from '../../utils/cycleTaskBulkUpload';
import { formatTasklistYesNoDisplayValue } from '../../utils/tasklistHelpers';
import { formatRequestTypeLabel } from '../../utils/bulkUploadValidation';
import { splitLocationNames } from '../../utils/locationName';

/** Shared suffixes; full ids are `${testIdPrefix}__${suffix}`. */
const BULK_UPLOAD_TEST_ID_SUFFIXES = {
  root: 'root',
  openButton: 'open-drawer-btn',
  drawer: 'drawer',
  dialog: 'dialog',
  close: 'close-btn',
  reuploadInput: 'reupload-input',
  body: 'body',
  step1: 'step-1',
  download: 'download-template',
  step2: 'step-2',
  reuploadButton: 'reupload-btn',
  validating: 'validating',
  inlineErrors: 'inline-errors',
  inlineErrorMessage: 'error-msg',
  headerErrors: 'header-errors',
  headerErrorsList: 'header-errors-list',
  headerError: 'header-error',
  rowErrors: 'row-errors',
  rowError: 'row-error',
  rowErrorMessage: 'row-error-msg',
  dropzone: 'dropzone',
  dragDrop: 'drag-drop',
  preview: 'preview',
  previewTable: 'preview-table',
  previewSimpleTable: 'preview-simple-table',
  previewRow: 'preview-row',
  avatar: 'avatar',
  errorFooter: 'error-footer',
  errorFooterMessage: 'error-summary',
  summaryErrors: 'summary-errors',
  errorFilterRow: 'show-errors-checkbox-row',
  errorFilter: 'show-errors-checkbox',
  cancel: 'cancel-btn',
  upload: 'upload-btn',
};

function buildBulkUploadTestIds(prefix, overrides = {}) {
  if (!prefix) return { ...(overrides || {}) };
  const ids = {};
  Object.keys(BULK_UPLOAD_TEST_ID_SUFFIXES).forEach((key) => {
    ids[key] = `${prefix}__${BULK_UPLOAD_TEST_ID_SUFFIXES[key]}`;
  });
  return { ...ids, ...(overrides || {}) };
}

const IMPORT_BLOCKED_CUSTOM_KEYS = new Set([
  'manager',
  'state',
  'customfields',
  'enforceThreshold',
]);
export default {
    name: 'BulkUpload',
    components: {
      BulkUploadDialog,
      BulkUploadFrame,
      OverflowList,
    },
    props: {
    uploadType: {
      type: String,
      validator: (value) => Object.values(BULK_CHECKLIST_UPLOAD_TYPE).includes(value),
      default: BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST,
    },
    project: {
      type: String,
    },
    cycle_type: {
      type: String,
    },
    customColumns: {
      type: Array,
      default: () => []
    },
    expectedHeadings: {
      type: Array,
      default: () => []
    },
    existingTrialBalanceData: {
      type: Array,
      default: () => []
    },
    hideButton: {
      type: Boolean,
      default: false
    },
    tableHeaders: {
      type: Array,
      default: () => []
    },
    buttonLabel: {
      type: String,
      default: 'Bulk Upload'
    },
    /**
     * Parent-owned mode for bulk task edit and information-request import.
     * Preview tables for known externalMode values are built into this component.
     */
    external: {
      type: Boolean,
      default: false,
    },
    /**
     * Built-in external preview + shell defaults.
     * - bulk-edit: TasklistDrawers bulk tasklist edit drawer
     * - information-request: InformationRequests import dialog
     */
    externalMode: {
      type: String,
      default: '',
      validator: (value) => ['', 'bulk-edit', 'information-request'].includes(value),
    },
    visible: {
      type: Boolean,
      default: false,
    },
    /** Vue 2 v-model alias for information-request dialog (value + input). */
    value: {
      type: Boolean,
      default: undefined,
    },
    container: {
      type: String,
      default: 'drawer',
      validator: (value) => ['drawer', 'dialog'].includes(value),
    },
    heading: {
      type: String,
      default: '',
    },
    step1Text: {
      type: String,
      default: '',
    },
    step2Heading: {
      type: String,
      default: '',
    },
    step2Text: {
      type: String,
      default: '',
    },
    previewReady: {
      type: Boolean,
      default: false,
    },
    validating: {
      type: Boolean,
      default: false,
    },
    fileName: {
      type: String,
      default: '',
    },
    showSelectedFileName: {
      type: Boolean,
      default: true,
    },
    reuploadVisible: {
      type: Boolean,
      default: false,
    },
    incomingFile: {
      type: [Array, Object, File],
      default: null,
    },
    resetDropzone: {
      type: Boolean,
      default: false,
    },
    accept: {
      type: String,
      default: '',
    },
    dropzoneAccept: {
      type: String,
      default: '',
    },
    singleFile: {
      type: Boolean,
      default: false,
    },
    hideDropzoneDetails: {
      type: Boolean,
      default: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    submitEnabled: {
      type: Boolean,
      default: false,
    },
    footerVisible: {
      type: Boolean,
      default: false,
    },
    errorFooterVisible: {
      type: Boolean,
      default: false,
    },
    errorFooterText: {
      type: String,
      default: '',
    },
    errorFooterNote: {
      type: String,
      default: '',
    },
    footerSummary: {
      type: Array,
      default: () => [],
    },
    summaryTight: {
      type: Boolean,
      default: false,
    },
    errorFilterVisible: {
      type: Boolean,
      default: false,
    },
    showErrorsOnly: {
      type: Boolean,
      default: false,
    },
    maxWidth: {
      type: String,
      default: '',
    },
    /**
     * Prefix for shared data-testid values (`${testIdPrefix}__${suffix}`).
     * Callers: tasklist-upload, tasklist-edit, information-request, trial-balance.
     */
    testIdPrefix: {
      type: String,
      default: '',
    },
    testIds: {
      type: Object,
      default: () => ({}),
    },
    // --- bulk-edit externalMode props (TasklistDrawers) ---
    fileDataList: {
      type: Array,
      default: () => [],
    },
    bulkEditFilteredData: {
      type: Array,
      default: () => [],
    },
    rowErrors: {
      type: Array,
      default: () => [],
    },
    errorResponse: {
      type: Array,
      default: () => [],
    },
    hasFileDataError: {
      type: Boolean,
      default: false,
    },
    hasValidationErrors: {
      type: Boolean,
      default: false,
    },
    legacyTaskTypeLabelChecklistName: {
      type: String,
      default: '',
    },
    legacyTaskTypeLabelChecklist: {
      type: String,
      default: '',
    },
    getNamesByIds: {
      type: Function,
      default: () => () => [],
    },
    getLocationById: {
      type: Function,
      default: () => () => '',
    },
    // --- information-request externalMode props ---
    requestUploadData: {
      type: Array,
      default: () => [],
    },
    errorFlag: {
      type: Boolean,
      default: false,
    },
    errorList: {
      type: Array,
      default: () => [],
    },
    requestHeaders: {
      type: Array,
      default: () => [],
    },
  },
    data: function () {
      return {
        BULK_CHECKLIST_UPLOAD_TYPE,
        drawerWidths,
        file: null,
        fileprocessing: {},
        resetTrigger: false,
        flag: false,
        tberrorflag: false,
        uploaddata: [],
        dataerror: [],
        dialog: false,
        loading: false,
        parsing: false,
        selectedFileName: '',
        showdatawitherror:false,
        errorsonly:[],
        headerErrorMessages: [],
        haserror: false,
        LEGACY_TASK_TYPE_LABEL_CHECKLIST_NAME,
        LEGACY_TASK_TYPE_LABEL_CHECKLIST,
        // information-request local shell state
        irFiles: [],
        irResetTrigger: false,
        irShowDataWithError: false,
      }
    },
    methods: {
      /**
       * Keeps drawer v-model and dialog v-model on the same open/close path.
       * External hosts listen for update:visible (drawer) or input (dialog).
       */
      setOpen(next) {
        const open = !!next;
        if (this.external) {
          this.$emit('update:visible', open);
          this.$emit('input', open);
          if (this.isInformationRequestMode && !open) this.resetInformationRequestShell();
          return;
        }
        this.dialog = open;
      },

      onCancel() {
        if (!this.external) {
          this.cleardata();
          return;
        }
        this.$emit('cancel');
        if (this.isDialog) this.setOpen(false);
      },

      onUpload() {
        if (this.external) {
          this.$emit('upload');
          return;
        }
        this.uploadData();
      },

      onDownload() {
        if (this.external) {
          this.$emit('download-template');
          return;
        }
        this.downloadTemplate();
      },

      onReupload() {
        if (this.isInformationRequestMode) {
          this.irFiles = [];
          this.selectedFileName = '';
          this.resetInformationRequestDropzone();
          this.$emit('reupload');
        } else if (this.external) {
          if (this.isBulkEditMode) this.selectedFileName = '';
          this.$emit('reupload');
        } else {
          this.resetUploadData();
        }
        this.$nextTick(() => {
          this.$refs.frame?.openFilePicker();
        });
      },

      onFilesChange(files) {
        if (!this.external) {
          this.updateFilesBulk(files);
          return;
        }
        if (this.isInformationRequestMode) {
          const file = Array.isArray(files) ? files[0] : files;
          this.irFiles = file ? [file] : [];
          this.selectedFileName = file?.name || '';
          if (file) this.$emit('file-selected', file);
          return;
        }
        this.$emit('update:file', files);
        const file = Array.isArray(files) ? files[0] : files;
        if (this.isBulkEditMode) this.selectedFileName = file?.name || '';
        if (file) this.$emit('file-selected', file);
      },

      onReuploadFileSelected(event) {
        const files = event?.target?.files;
        if (!files?.length) return;
        if (this.isInformationRequestMode) {
          this.irFiles = [files[0]];
          this.selectedFileName = files[0].name || '';
          this.$emit('file-selected', files[0]);
        } else if (this.external) {
          if (this.isBulkEditMode) this.selectedFileName = files[0].name || '';
          this.$emit('update:file', [files[0]]);
          this.$emit('file-selected', files[0]);
        } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          this.updateFilesBulk(files);
        } else {
          this.updateFilesBulk([files[0]]);
        }
        if (event.target) event.target.value = '';
      },

      onShowErrorsOnly(value) {
        if (this.isInformationRequestMode) {
          this.irShowDataWithError = value;
          return;
        }
        if (this.external) {
          this.$emit('update:showErrorsOnly', value);
          this.$emit('update:showDataWithError', value);
          return;
        }
        this.showdatawitherror = value;
      },

      resetInformationRequestShell() {
        this.irFiles = [];
        this.selectedFileName = '';
        this.irShowDataWithError = false;
        this.resetInformationRequestDropzone();
      },

      resetInformationRequestDropzone() {
        this.irResetTrigger = false;
        this.$nextTick(() => {
          this.irResetTrigger = true;
        });
      },

      requestRowHasError(row) {
        const errors = row?.data?.[0]?.errors;
        if (Array.isArray(errors)) return errors.length > 0;
        return !!errors && String(errors).trim() !== '';
      },

      requestFieldError(row, field) {
        const message = row?.data?.[0]?.fieldErrors?.[field];
        return message ? String(message) : '';
      },

      formatRequestType(row) {
        if (!row) return '';
        return formatRequestTypeLabel(row.type);
      },

      bulkEditPreviewColumnKeys() {
        return (this.tableHeaders || []).map((header) => header.value).filter(Boolean);
      },

      bulkEditUnmappedRowErrorText(index) {
        return pickUnmappedRowFieldErrorMessages(
          this.rowErrors[index] || {},
          this.bulkEditPreviewColumnKeys(),
        ).join('; ');
      },

      /** Field error for a bulk-edit cell. Unknown API keys surface on the first column. */
      bulkEditCellError(index, field) {
        const fields = this.rowErrors[index] || {};
        let message = getRowFieldErrorMessage(fields, field);
        const firstField = this.bulkEditPreviewColumnKeys()[0];
        if (field === firstField) {
          const unmapped = this.bulkEditUnmappedRowErrorText(index);
          if (unmapped) {
            message = [message, unmapped].filter(Boolean).join('; ');
          }
        }
        return message || '';
      },

      bulkEditPreviewCellClass(index, header) {
        const classes = [];
        if (header.value === 'description') {
          classes.push('bulk-import-dialog__cell--truncate');
        }
        if (this.bulkEditCellError(index, header.value)) {
          classes.push('bulk-import-dialog__cell--error');
        }
        return classes;
      },

      isBulkEditUserRoleColumn(column) {
        return ['preparer', 'approver', 'sign_off_approver'].includes(column);
      },

      isBulkEditOverflowColumn(column) {
        return BULK_EDIT_OVERFLOW_COLUMNS.includes(column);
      },

      isChecklistOverflowColumn(column) {
        return ['tags', 'location', 'assertions', 'glcode', 'preparer', 'approver', 'sign_off_approver'].includes(column);
      },

      previewItemLabel(item) {
        if (item == null) return '';
        if (typeof item === 'string' || typeof item === 'number') return String(item).trim();
        return String(item.name || item.glcode || item.ref || item.label || '').trim();
      },

      previewList(value) {
        if (Array.isArray(value)) {
          return value.map((item) => this.previewItemLabel(item)).filter(Boolean);
        }
        if (value == null || value === '') return [];
        if (typeof value === 'object') {
          const label = this.previewItemLabel(value);
          return label ? [label] : [];
        }
        const text = String(value).trim();
        if (!text) return [];
        if (text.includes(',')) {
          return text.split(',').map((part) => part.trim()).filter(Boolean);
        }
        return [text];
      },

      bulkEditOverflowItems(row, column) {
        if (this.isBulkEditUserRoleColumn(column)) {
          return (this.getNamesByIds(this.getBulkEditUserRoleValues(row, column)) || []).filter(Boolean);
        }
        if (column === 'location') {
          const raw = row?.[column];
          const ids = Array.isArray(raw) ? raw : (raw ? [raw] : []);
          return ids
            .map((id) => this.getLocationById(id) || this.previewItemLabel(id))
            .filter(Boolean);
        }
        return this.previewList(row?.[column]);
      },

      checklistOverflowItems(row, column) {
        return this.previewList(row?.[column]);
      },

      getBulkEditUserRoleValues(row, column) {
        const value = row?.[column];
        if (Array.isArray(value) && value.length) return value;
        if (column === 'sign_off_approver' && Array.isArray(row?.sign_off_approvers)) {
          return row.sign_off_approvers;
        }
        return value || [];
      },
      // ============================================
      // SHARED ERROR HANDLING UTILITIES
      // ============================================
      
      createTrialBalanceErrorRow() {
        return ['', '', '', '', '', ''];
      },

      ensureTrialBalanceErrorRow(dataerror, rowIndex) {
        if (!dataerror[rowIndex]) {
          dataerror[rowIndex] = [this.createTrialBalanceErrorRow()];
          return dataerror[rowIndex][0];
        }
        if (!dataerror[rowIndex][0]) {
          dataerror[rowIndex][0] = this.createTrialBalanceErrorRow();
          return dataerror[rowIndex][0];
        }
        const rowErrors = dataerror[rowIndex][0];
        while (rowErrors.length < 6) {
          rowErrors.push('');
        }
        return rowErrors;
      },

      trialBalanceRowMissingLocation(row) {
        const location = row?.location;
        const hasRoot = Array.isArray(location)
          ? location.some((id) => id != null && String(id).trim() !== '')
          : location != null && String(location).trim() !== '';
        if (hasRoot) return false;
        if (Array.isArray(row?.location_detail) && row.location_detail.length > 0) return false;
        return true;
      },

      getTrialBalanceEmptyLocationAccountNos() {
        const emptyLocationAccNos = new Set();
        for (const row of this.existingTrialBalanceData || []) {
          if (this.trialBalanceRowMissingLocation(row)) {
            emptyLocationAccNos.add(String(row.glcode));
          }
        }
        return emptyLocationAccNos;
      },

      applyTrialBalanceDuplicateLocationErrors() {
        const emptyLocationAccNos = this.getTrialBalanceEmptyLocationAccountNos();
        let errorCount = 0;

        for (let idx = 0; idx < this.uploaddata.length; idx++) {
          const row = this.uploaddata[idx];
          const accNo = (row.data[0] || {}).glcode;
          const location = (row.data[0] || {}).location;
          if ((!location || location === '') && emptyLocationAccNos.has(String(accNo))) {
            const rowErrors = this.ensureTrialBalanceErrorRow(this.dataerror, idx);
            rowErrors[5] = TRIAL_BALANCE_VALIDATION_MESSAGES.DUPLICATE_ACCOUNT_EMPTY_LOCATION;
            errorCount += 1;
          }
        }

        return errorCount;
      },

      syncTrialBalanceErrorsOnly() {
        const flattenedErrors = [];
        (this.dataerror || []).forEach((groupErrors, rowIndex) => {
          const rowErrorArray = groupErrors?.[0] || [];
          const errorObj = {};
          rowErrorArray.forEach((msg, idx) => {
            if (msg && String(msg).trim() !== '') {
              errorObj[`error_${idx}`] = `Row ${rowIndex + 1}: ${msg}`;
            }
          });
          if (Object.keys(errorObj).length) {
            flattenedErrors.push(errorObj);
          }
        });
        this.errorsonly = flattenedErrors;
      },

      getTrialBalanceCellError(rowIndex, header) {
        if (header.errorIndex === undefined) return '';
        const message = this.dataerror[rowIndex]?.[0]?.[header.errorIndex];
        return message && String(message).trim() !== '' ? message : '';
      },

      trialBalanceCellClass(rowIndex, header) {
        const classes = [];
        if (this.getTrialBalanceCellError(rowIndex, header)) {
          classes.push('bulk-import-dialog__cell--error', 'all-width', 'all-height', 'border-red');
        }
        return classes;
      },
      
      /**
       * Show error in drawer with proper upload type context
       * Used for critical errors that prevent data upload
       * @param {string} errorMessage - The error message to display
       * @param {boolean} isHeaderError - If true, shows under "Header Issues Detected", otherwise under "Data Errors"
       */
      showErrorInDrawer(errorMessage, isHeaderError = false) {
        this.haserror = true;
        
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          this.tberrorflag = true;
        } else {
          this.flag = true;
        }
        
        this.uploaddata = [];
        this.dataerror = [];
        
        // Show in appropriate section based on error type
        if (isHeaderError) {
          this.headerErrorMessages = [errorMessage];
          this.errorsonly = [];
        } else {
          this.errorsonly = [{ error_0: errorMessage }];
          this.headerErrorMessages = [];
        }
        
        this.dialog = true;
      },
      
      /**
       * Reset all upload-related data and error states
       * Common for both checklist and trial balance
       */
      resetUploadData() {
        this.uploaddata = [];
        this.showdatawitherror = false;
        this.file = null;
        this.fileprocessing = {};
        this.parsing = false;
        this.selectedFileName = '';
        this.dataerror = [];
        this.flag = false;
        this.errorsonly = [];
        this.headerErrorMessages = [];
        this.haserror = false;
        this.tberrorflag = false;
        this.resetDragDrop();
      },

      resetDragDrop() {
        this.resetTrigger = false;
        this.$nextTick(() => {
          this.resetTrigger = true;
        });
      },


      // ============================================
      // DRAWER & UI CONTROL METHODS
      // ============================================
      
      /**
       * Opens the drawer. Trial balance calls this through a template ref.
       */
      openBulkUpload() {
        if (this.external) {
          this.setOpen(true);
          return;
        }
        this.resetUploadData();
        this.dialog = true;
      },

      
      /**
       * Download appropriate template based on upload type
       */
      downloadTemplate(){
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          if(this.project) {
            this.$nova.downloadFile('', {}, `/v2/financialclose/analysis/getbulkchecklisttemplate/${this.project ? this.project : ''}`);
          } else {
            this.$nova.downloadFile('', {}, `/v2/financialclose/masters/checklist/getbulksubmittemplate/${this.cycle_type ? `${this.cycle_type}` : ''}`);
          }
        } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          this.$nova.downloadFile('', {}, '/v2/financialclose/analysis/getuploadtbtemplate');
        }
      },
      
      /**
       * Clear all data and close drawer
       */
      /** Clears the managed upload and closes the drawer. */
      cleardata() {
        this.resetUploadData();
        this.dialog = false;
        this.loading = false;
      },
      
      // ============================================
      // FILE READING & PROCESSING
      // ============================================
      
      /**
       * Main file reading dispatcher - routes to appropriate handler
       */
      async readFile(key) {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          await this.readChecklistFile(key);
        } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          await this.readTrialBalanceFile(key);
        }
      },
      /**
       * Read and validate checklist Excel file
       * Processes checklist-specific columns and validation rules
       */
      async readChecklistFile(key) {
        this.headerErrorMessages = [];
        
        // ===== CHECKLIST ERROR UTILITIES =====
        
        // Utility: add (or aggregate) error message(s) for a given field
        const addError = (rowErrorBag, fieldName, message = " ") => {
          if (!rowErrorBag[fieldName]) {
            rowErrorBag[fieldName] = message;
          } else {
            rowErrorBag[fieldName] = Array.isArray(rowErrorBag[fieldName])
              ? [...rowErrorBag[fieldName], message]
              : [rowErrorBag[fieldName], message];
          }
        };

        // Utility: normalize error objects so array messages become joined text (for table display)
        const normalizeErrorData = (rawErrorArray) => {
              return rawErrorArray.map(errorObject => {
            const normalized = {};
            Object.keys(errorObject).forEach(fieldKey => {
              normalized[fieldKey] = Array.isArray(errorObject[fieldKey])
                ? errorObject[fieldKey].join(',')
                : errorObject[fieldKey];
            });
            return normalized;
          });
        };


        const currentFile = this.file;
        if ((currentFile || {}).name && (this.fileprocessing[key] || 0) === 0) {
          // ===== CHECKLIST VALIDATION SETUP =====
          // Base headings (in strict order) expected from template
          // Append custom column display names with date suffix for date-type columns
          const customColumnHeadings = this.importCustomColumns.map((customColumn) =>
            customColumn.dataType === 'date'
              ? `${customColumn.displayText} (dd/mm/yyyy)`
              : customColumn.displayText
          ) || [];
          
          let expectedHeadings = [
            ...this.expectedHeadings,
            ...customColumnHeadings
          ];
      
          this.fileprocessing[key] = 1;
          this.parsing = true;
          this.selectedFileName = currentFile.name || '';
          this.uploaddata = [];
          this.dataerror = [];

          const fileExtension = ((currentFile || {}).name || "").split(".").pop();

          Promise.resolve()
        // 1. Validate extension
        .then(() => {
          const allowedExtensions = ["xlsx", "xls", "xlsm", "xlsb"];
          if (allowedExtensions.indexOf(fileExtension) === -1) {
            this.uploaddata = [];
            this.dataerror = [{ description: GENERAL_ERROR_MESSAGES.EXTENSION_NOT_SUPPORTED }];
            this.errorsonly = this.dataerror;
            this.flag = true;
            throw new Error(GENERAL_ERROR_MESSAGES.EXTENSION_NOT_SUPPORTED);
          }
          // 2. Read the Excel sheet "TOP SHEET"
          return this.$nova.readExcelFile(currentFile, "TOP SHEET");
        })
        // 3. Process sheet data
        .then(async (sheetData) => {
          const headerRow = sheetData[0] || [];
          const headerIssues = {};

          // Validate each expected heading by index
          expectedHeadings.forEach((expectedTitle, positionIndex) => {
            const incomingTitle = headerRow[positionIndex];
            if (!incomingTitle) {
          addError(headerIssues, expectedTitle, HEADER_ERROR_MESSAGES.MISSING_COLUMN(expectedTitle, positionIndex + 1));
            } else if (canonicalizeBulkTaskHeader(incomingTitle) !== canonicalizeBulkTaskHeader(expectedTitle)) {
          addError(
            headerIssues,
            expectedTitle,
            HEADER_ERROR_MESSAGES.WRONG_COLUMN(expectedTitle, incomingTitle, positionIndex + 1)
          );
            }
          });

          // Extra unexpected columns beyond what is defined
          if (headerRow.length > expectedHeadings.length) {
            for (let unexpectedIndex = expectedHeadings.length; unexpectedIndex < headerRow.length; unexpectedIndex++) {
          addError(
            headerIssues,
            'description',
            HEADER_ERROR_MESSAGES.EXTRA_COLUMN(headerRow[unexpectedIndex], unexpectedIndex + 1)
          );
            }
          }

          // If header problems exist, stop further processing
          if (Object.keys(headerIssues).length > 0) { 
            // Build flat list of header messages
            this.headerErrorMessages = Object.values(headerIssues)
              .map(v => Array.isArray(v) ? v : [v])
              .flat()
              .filter(Boolean);
            this.flag = true;
            this.uploaddata = [];
            this.dataerror = [];
            this.errorsonly = [];
            return;
          }

          const parsedRows = [];
          const perRowErrors = [];
          this.flag = false;

          // Iterate through each data row (skip header at index 0)
          for (let rowIndex = 1; rowIndex < sheetData.length; rowIndex++) {
            if (!sheetData[rowIndex] || sheetData[rowIndex].length === 0) continue;

            const rowCells = sheetData[rowIndex];
            let rowData = {
          task_type: "",
          description: rowCells[0],
          category: (rowCells[1] || "").toString().trim(),
          subcategory: (rowCells[2] || "").toString().trim()
            };

            // Parse list-type fields (split by commas, trim empties)
            // Utility to parse comma-separated fields into trimmed, non-empty arrays
            rowData.location    = splitLocationNames(rowCells[3]);
            rowData.assertions  = parseCommaSeparatedField(rowCells[4]);
            rowData.purpose     = rowCells[5];
            rowData.instruction = rowCells[6];
            rowData.preparer    = parseCommaSeparatedField(rowCells[7]);
            rowData.approver    = parseCommaSeparatedField(rowCells[8]);
            rowData.sign_off_approver = parseCommaSeparatedField(rowCells[9]);
            rowData.requires_sign_off = rowData.sign_off_approver.length > 0;
            rowData.tags        = parseCommaSeparatedField(rowCells[10]);
            rowData.duedays     = (rowCells[11] !== undefined && rowCells[11] !== null && rowCells[11] !== "")
              ? Number(rowCells[11])
              : null;
            rowData.glcode      = parseCommaSeparatedField(rowCells[12]);
            rowData.priority    = rowCells[13] || "Medium";
            rowData.no_of_evidence = Number(rowCells[14] || "") || 0;
            rowData.evidence_required = coerceEvidenceRequired(rowCells[15]);
            const enforceText = (rowCells[16] || '').toString().trim().toLowerCase();
            const enforceThresholdRaw = !['no', 'false', '0'].includes(enforceText);
            rowData.auto_approve = ['yes', 'true', '1'].includes((rowCells[17] || '').toString().trim().toLowerCase()) || rowCells[17] === true || rowCells[17] === 1;

            // Derive task type based on presence of GL Codes
            rowData.task_type = rowData.glcode.length
          ? this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION
          : this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.NORMAL;
            rowData.enforceThreshold = rowData.task_type
              === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION
              ? enforceThresholdRaw
              : false;

            let customFieldObjects = [];
            const rowValidationErrors = {};

            // Validate and attach custom columns (if any)
            if (this.importCustomColumns.length > 0) {
              customFieldObjects = this.importCustomColumns.map((customColumn) => {
                // Search for header with date suffix if it's a date column
                let headerSearchValue = customColumn.displayText;
                if (customColumn.dataType === 'date') {
                  headerSearchValue = `${customColumn.displayText} (dd/mm/yyyy)`;
                }
                
                const customColumnIndex = expectedHeadings.indexOf(headerSearchValue);
                let rawValue;
                if(customColumn.dataType === 'date' ){
                   rawValue = customColumnIndex >= 0 ? formatDateToDMY(rowCells[customColumnIndex]) : '';
                } else{
                    rawValue = customColumnIndex >= 0 ? rowCells[customColumnIndex] : '';
                }
                let formattedValue = rawValue || '';
                if (formattedValue) {
                  if (customColumn.dataType === 'number' && isNaN(Number(formattedValue))) {
                    addError(rowValidationErrors, customColumn.columnName, `'${formattedValue}' ${CHECKLIST_VALIDATION_MESSAGES.INVALID_NUMBER}`);
                  } else if (customColumn.dataType === 'date' && !parseDueDateValue(formattedValue)) {
                    addError(rowValidationErrors, customColumn.columnName, `'${formattedValue}' ${CHECKLIST_VALIDATION_MESSAGES.INVALID_DATE}`);
                  }
                }
                return { [customColumn.columnName]: formattedValue };
              });

              // Flatten custom fields into rowData
              rowData = {
                ...rowData,
                ...customFieldObjects.reduce((accumulator, fieldObject) => ({ ...accumulator, ...fieldObject }), {})
              };
            }
            rowData.customfields = customFieldObjects;

            // Required field checks
            if ((rowData.description || "").toString() === "") addError(rowValidationErrors, "description", CHECKLIST_VALIDATION_MESSAGES.DESCRIPTION_REQUIRED);
            if ((rowData.category || "").toString() === "") addError(rowValidationErrors, "category", CHECKLIST_VALIDATION_MESSAGES.CATEGORY_REQUIRED);
            if ((rowData.subcategory || "").toString() === "") addError(rowValidationErrors, "subcategory", CHECKLIST_VALIDATION_MESSAGES.SUBCATEGORY_REQUIRED);
            if (rowData.location.length === 0) addError(rowValidationErrors, "location", CHECKLIST_VALIDATION_MESSAGES.LOCATION_REQUIRED);
            if (rowData.preparer.length === 0) addError(rowValidationErrors, "preparer", CHECKLIST_VALIDATION_MESSAGES.PREPARER_REQUIRED);
            if (rowData.approver.length === 0) addError(rowValidationErrors, "approver", CHECKLIST_VALIDATION_MESSAGES.APPROVER_REQUIRED);
            const duplicatePreparers = findDuplicateValues(rowData.preparer);
            if (duplicatePreparers.length) {
              addError(rowValidationErrors, "preparer", duplicateInRoleMessage(duplicatePreparers, 'preparer'));
            }
            const duplicateApprovers = findDuplicateValues(rowData.approver);
            if (duplicateApprovers.length) {
              addError(rowValidationErrors, "approver", duplicateInRoleMessage(duplicateApprovers, 'approver'));
            }
            const duplicateSignOff = findDuplicateValues(rowData.sign_off_approver);
            if (duplicateSignOff.length) {
              addError(rowValidationErrors, "sign_off_approver", duplicateInRoleMessage(duplicateSignOff, 'sign-off approver'));
            }
            // Frequency validation - required for master checklist, optional for project-level
            if (!this.project && (rowData.tags || []).length === 0) {
              addError(rowValidationErrors, "tags", CHECKLIST_VALIDATION_MESSAGES.TAGS_REQUIRED);
            }
            // Keep tags for all contexts (master and project)

            if (rowData.preparer.length && rowData.approver.length) {
              const duplicateUsers = rowData.preparer.filter((preparerUser) =>
                rowData.approver.some(
                  (approverUser) =>
                    preparerUser.trim().toLowerCase() === approverUser.trim().toLowerCase()
                )
              );
              if (duplicateUsers.length > 0) {
                addError(
                  rowValidationErrors,
                  "preparer",
                  `'${duplicateUsers.join(",")}' ${CHECKLIST_VALIDATION_MESSAGES.PREPARER_APPROVER_OVERLAP}`
                );
              }
            }

          const signOffApprovers = rowData.sign_off_approver || [];
          if (signOffApprovers.length > 0) {
            if (!rowData.approver.length) {
              addError(rowValidationErrors, "sign_off_approver", SIGN_OFF.ERRORS.ORPHAN_L2);
            }
            const preparerOverlap = signOffApprovers.filter((email) =>
              rowData.preparer.some((p) => p.trim().toLowerCase() === String(email).trim().toLowerCase())
            );
            if (preparerOverlap.length > 0) {
              addError(rowValidationErrors, "sign_off_approver", SIGN_OFF.ERRORS.PREPARER_SOD);
            }
            const approverOverlap = signOffApprovers.filter((email) =>
              rowData.approver.some((a) => a.trim().toLowerCase() === String(email).trim().toLowerCase())
            );
            if (approverOverlap.length > 0) {
              addError(rowValidationErrors, "sign_off_approver", SIGN_OFF.ERRORS.APPROVER_SOD);
            }
          }

            // Due days required validation
            if (rowData.duedays === null || rowData.duedays === '') {
              addError(rowValidationErrors, "duedays", "Due Days is required");
            }

            // Due days numeric validation
            if (
          rowData.duedays !== null &&
          (isNaN(rowData.duedays) || !Number.isFinite(rowData.duedays))
            ) {
          addError(rowValidationErrors, "duedays", `'${rowCells[11]}' ${CHECKLIST_VALIDATION_MESSAGES.INVALID_NUMBER}`);
            }

            // Due days range validation (-200 to 200)
            if (
          rowData.duedays !== null &&
          !isNaN(rowData.duedays) &&
          Number.isFinite(rowData.duedays) &&
          (rowData.duedays < -200 || rowData.duedays > 200)
            ) {
          addError(rowValidationErrors, "duedays", CHECKLIST_VALIDATION_MESSAGES.DUE_DAYS_OUT_OF_RANGE);
            }

            if (isNaN(rowData.no_of_evidence) || rowData.no_of_evidence < 0 || rowData.no_of_evidence > 10) {
              addError(rowValidationErrors, 'no_of_evidence', 'No. of Evidences must be between 0 and 10');
            } else if (isEvidenceRequiredEnabled(rowData.evidence_required) && rowData.no_of_evidence < 1) {
              addError(rowValidationErrors, 'no_of_evidence', 'When Evidence Required is Yes, No. of Evidences must be between 1 and 10');
            }

            if (Object.keys(rowValidationErrors).length > 0) {
          this.flag = true;
            }

            parsedRows.push(rowData);
            perRowErrors.push(rowValidationErrors);
          }

          // Server validates before revealing preview (drop-zone spinner stays up).
          let mergedErrors = normalizeErrorData(perRowErrors);
          if (this.project && parsedRows.length) {
            const validation = await this.$store.dispatch('tasks/validateCycleTaskList', {
              checklistId: this.project,
              data: parsedRows.map((a) => ({ ...a, cycle_type: this.cycle_type })),
              mode: 'bulkAdd',
            });
            const parsed = parseCycleTaskValidateResult(validation);
            if (!parsed.valid) {
              const { errors, hasErrors } = mergeCycleTaskRowErrors(
                mergedErrors,
                parsed.rowErrors,
                'object',
              );
              if (hasErrors) {
                mergedErrors = errors;
                this.flag = true;
                this.haserror = true;
              }
            }
          }

          this.uploaddata = parsedRows;
          this.dataerror = mergedErrors;
          this.errorsonly = this.showdatawitherror
            ? this.dataerror.filter((item) => Object.keys(item || {}).length > 0)
            : this.dataerror;
          this.dialog = true;
        })
        // Catch-all (excluding controlled "handled" stop)
        .catch(errorObject => {
          if (!errorObject) {
            // Silently ignore controlled flow interruption
          } else {
            this.$store.commit("sbError", errorObject.message || errorObject || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR);
            console.error('[BulkUpload]', errorObject);
          }
        })
        // Always reset processing state
        .finally(() => {
          this.file = null;
          this.fileprocessing[key] = 0;
          this.parsing = false;
        });
        }
      },
      /**
       * Read and validate trial balance Excel file
       * Processes trial balance-specific columns and validation rules
       */
      async readTrialBalanceFile(key) {
        let file = this.file[0];

        if ((file || {}).name && (this.fileprocessing[key] || 0) === 0) {
          this.fileprocessing[key] = 1;
          this.parsing = true;
          this.selectedFileName = file.name || '';
          this.uploaddata = [];
          this.dataerror = [];
          let ext = ((file || {}).name || '').split('.').pop();

          try {
            if (['xlsx', 'xls', 'xlsm', 'xlsb'].indexOf(ext) === -1) {
              throw new Error(GENERAL_ERROR_MESSAGES.EXTENSION_NOT_SUPPORTED);
            }

            const dt2 = await this.$nova.readExcelFile(file);
            
            // Normalize headers for comparison (trim spaces, handle case)
            const actualHeaders = (dt2[0] || []).map(h => (h || '').toString().trim());
            const expectedHeaders = [
              'Account No',
              'Opening Amount',
              'Transaction',
              'Audit Entries',
              'Closing Amount',
              'Location'
            ];
            
            // Check if headers match
            const headersMatch = JSON.stringify(actualHeaders) === JSON.stringify(expectedHeaders);
            
            if (headersMatch) {
              let grouplist = [];
              for (let i = 0; i < dt2.length; i++) {
                if (i > 0) {
                  const el = dt2[i];
                  let gname = (el[0] || '').toString();
                  if (gname !== '' && grouplist.indexOf(gname) === -1) grouplist.push(gname);
                }
              }
              let groupdata = {};
              let grouporder = [];
              let errordata = {};
              this.tberrorflag = false;
              let gstartnum = 1;
              for (let i = 0; i < dt2.length; i++) {
                if (i > 0) {
                  const el = dt2[i];
                  let gname = ''.toString();
                  if (gname === '') {
                    let newgname = 'g' + (gstartnum || '').toString();
                    while (grouplist.indexOf(newgname) !== -1) {
                      newgname = 'g' + (gstartnum || '').toString();
                      gstartnum = gstartnum + 1;
                    }
                    gname = newgname;
                    if (grouplist.indexOf(gname) === -1) grouplist.push(gname);
                  }
                  groupdata[gname] = groupdata[gname] || [];
                  groupdata[gname].push({
                    group: gname,
                    glcode: el[0],
                    opening_amount: el[1],
                    transaction: el[2],
                    audit_entries: el[3],
                    closing_amount: el[4],
                    location: el[5]
                  });
                  if (gname !== '' && grouplist.indexOf(gname) === -1) grouplist.push(gname);
                  if (grouporder.indexOf(gname) === -1) grouporder.push(gname);
                  errordata[gname] = errordata[gname] || [];
                  const errors = this.createTrialBalanceErrorRow();
                  const glcode = (el[0] || '').toString();
                  if (glcode === '') errors[1] = TRIAL_BALANCE_VALIDATION_MESSAGES.GL_CODE_REQUIRED;

                  if (errors.some((msg) => msg && String(msg).trim() !== '')) this.tberrorflag = true;
                  errordata[gname].push(errors);
                }
              }
              // Build preview locally; assign only after client + server validation so the
              // drop-zone spinner stays up (no flash of an unvalidated table).
              const previewRows = [];
              const previewErrors = [];
              for (const i of grouporder) {
                previewRows.push({ group: i, data: groupdata[i] });
                previewErrors.push(errordata[i]);
              }

              const previousUpload = this.uploaddata;
              const previousErrors = this.dataerror;
              this.uploaddata = previewRows;
              this.dataerror = previewErrors;

              try {
                const duplicateLocationErrors = this.applyTrialBalanceDuplicateLocationErrors();
                if (duplicateLocationErrors > 0) {
                  this.tberrorflag = true;
                }

                if (this.project && previewRows.length) {
                  await this.validateTrialBalancePreview();
                }

                this.syncTrialBalanceErrorsOnly();
              } catch (validateErr) {
                this.uploaddata = previousUpload;
                this.dataerror = previousErrors;
                throw validateErr;
              }

              this.dialog = true;
            } else {
              // Show header mismatch errors one by one
              const headerErrors = [];
              headerErrors.push(HEADER_ERROR_MESSAGES.HEADER_MISMATCH);
              
              // Show expected headers
              headerErrors.push(HEADER_ERROR_MESSAGES.EXPECTED_HEADERS(expectedHeaders));
              
              // Show found headers
              headerErrors.push(HEADER_ERROR_MESSAGES.FOUND_HEADERS(actualHeaders));
              
              // Additional instruction
              headerErrors.push(HEADER_ERROR_MESSAGES.ENSURE_ORDER);
              
              // Set header error messages and flags
              this.headerErrorMessages = headerErrors;
              this.haserror = true;
              this.tberrorflag = true;
              this.uploaddata = [];
              this.dataerror = [];
              this.errorsonly = [];
              this.dialog = true;
            }
          } catch (err) {
            // Show all errors in drawer (non-header errors)
            const errorMsg = err.message || err || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR;
            this.showErrorInDrawer(errorMsg, false); // false = data error
            console.error('[BulkUpload]', err);
          } finally {
            this.file = null;
            this.fileprocessing[key] = 0;
            this.parsing = false;
          }
        }
      },
      // ============================================
      // DATA UPLOAD METHODS
      // ============================================
      
      /**
       * Main upload dispatcher - routes to appropriate upload handler
       */
      uploadData() {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          this.uploadChecklistData();
        } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          this.uploadTrialBalanceData();
        }
      },
      
      /**
       * Upload checklist data to server (validate-then-write via tasks store when project/cycle set).
       */
      uploadChecklistData() {
        this.loading = true;
        const data = this.uploaddata.map((a) => ({ ...a, cycle_type: this.cycle_type }));
        this.$store.dispatch('tasks/validateAndWriteCycleTaskList', {
          checklistId: this.project || undefined,
          data,
          mode: 'bulkAdd',
        })
          .then((result) => {
            if (!result.ok) {
              this.applyValidateRowErrors(result.rowErrors);
              throw new Error(result.message || GENERAL_ERROR_MESSAGES.ERROR_UPLOADING_CHECKLIST);
            }
            const writeData = result.writeData || {};
            if (writeData.status === 'success') {
              this.dialog = false;
              this.$emit('reload');
              this.$store.commit('sbSuccess', 'Tasklist uploaded successfully');
            } else {
              throw writeData.message || GENERAL_ERROR_MESSAGES.ERROR_UPLOADING_CHECKLIST;
            }
          })
          .catch((err) => {
            this.$store.commit('sbError', err.message || err || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR);
            console.error('[BulkUpload]', err);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      /**
       * Merge server validate rowErrors onto the checklist preview grid.
       */
      applyValidateRowErrors(rowErrors = []) {
        const { errors, hasErrors } = mergeCycleTaskRowErrors(
          this.dataerror,
          rowErrors,
          'object',
        );
        if (!hasErrors) return;
        this.dataerror = errors;
        this.errorsonly = this.showdatawitherror
          ? this.dataerror.filter((item) => Object.keys(item || {}).length > 0)
          : this.dataerror;
        this.flag = true;
        this.haserror = true;
      },

      /**
       * Call validateBulkTb and map rowErrors onto the TB preview error arrays.
       */
      async validateTrialBalancePreview() {
        try {
          const rows = this.uploaddata.map((row) => row.data[0]);
          const validation = await this.$store.dispatch('tasks/validateBulkTb', {
            projectId: this.project,
            data: rows,
          });
          if (validation?.status === 'failure') {
            this.$store.commit(
              'sbError',
              validation.message || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR,
            );
            this.tberrorflag = true;
            return;
          }
          const rowErrors = Array.isArray(validation?.rowErrors) ? validation.rowErrors : [];
          if (validation?.valid === false || rowErrors.length) {
            this.applyTbValidateRowErrors(rowErrors);
          }
        } catch (err) {
          console.error('[BulkUpload] validateBulkTb failed:', err);
          this.$store.commit(
            'sbError',
            err?.response?.data?.message || err?.message || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR,
          );
          this.tberrorflag = true;
        }
      },

      applyTbValidateRowErrors(rowErrors = []) {
        if (!Array.isArray(rowErrors) || !rowErrors.length) return;
        rowErrors.forEach((entry) => {
          const idx = Number(entry.row) - 1;
          if (!Number.isFinite(idx) || idx < 0) return;
          const rowErrorsArr = this.ensureTrialBalanceErrorRow(this.dataerror, idx);
          const fields = entry.fields || {};
          if (fields.glcode) rowErrorsArr[1] = fields.glcode;
          if (fields.location) rowErrorsArr[5] = fields.location;
          if (fields._row) rowErrorsArr[0] = fields._row;
        });
        this.tberrorflag = true;
        this.syncTrialBalanceErrorsOnly();
      },
      /**
       * Upload trial balance data to server
       * Includes validation for duplicate accounts with empty locations
       */
      uploadTrialBalanceData() {
        const duplicateLocationErrors = this.applyTrialBalanceDuplicateLocationErrors();
        if (duplicateLocationErrors > 0) {
          this.tberrorflag = true;
          this.syncTrialBalanceErrorsOnly();
          this.loading = false;
          return;
        }
        this.loading = true;
        const rows = [];
        for (const i of this.uploaddata) {
          rows.push(i.data[0]);
        }
        this.$store.dispatch('trialBalance/bulkAddTb', {
          projectId: this.project,
          data: rows,
        })
          .then((result) => {
            if (!result || result.outcome === 'error') return;
            if (result.outcome === 'validation') {
              if (Array.isArray(result.rowErrors) && result.rowErrors.length) {
                this.applyTbValidateRowErrors(result.rowErrors);
              }
              this.$store.commit('sbError', result.message || GENERAL_ERROR_MESSAGES.ERROR_UPLOADING_TB);
              return;
            }
            if (result.outcome === 'started' || result.outcome === 'conflict') {
              this.dialog = false;
              console.log('[BulkUpload]:', { outcome: result.outcome, queueId: result.tbUpload && result.tbUpload.queueId });
              return;
            }
            if (result.outcome === 'completed') {
              this.dialog = false;
              this.$emit('reload');
              this.$store.commit('sbSuccess', 'TB uploaded successfully');
            }
          })
          .catch((err) => {
            this.$store.commit('sbError', err.message || err || GENERAL_ERROR_MESSAGES.UNKNOWN_ERROR);
            console.error('[BulkUpload]:', err);
          })
          .finally(() => {
            this.loading = false;
          });
      },
      // ============================================
      // UTILITY & HELPER METHODS
      // ============================================
      
      /**
       * Update files from drag-drop component
       * Handles different file formats for checklist vs trial balance
       */
      updateFilesBulk(newFilesBulk) {
        this.file = this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE ? newFilesBulk : newFilesBulk[0];
        this.readFile('file')
      },
      
      /**
       * Get cell value from row based on upload type structure
       */
      getCellValue(row, key) {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          return (row.data && row.data[0]) ? row.data[0][key] : '';
        }
        return row[key] || '';
      },
      
      /**
       * Format cell value based on specified format type
       */
      formatCellValue(value, format) {
        if (format === 'number') {
          return this.formatNum(value);
        }
        return value;
      },

      formatPreviewCellValue(fieldKey, value) {
        return formatTasklistYesNoDisplayValue(fieldKey, value);
      },
      
      /**
       * Format number with locale-specific formatting
       */
      formatNum(input) {
        let number = Number(input);
        if (isNaN(number) || input === null || input === undefined || input === '') {
          return '';
        }
        return number.toLocaleString('en-US');
      },

      checklistPreviewColumnKeys() {
        return (this.previewTableHeaders || []).map((h) => h.value).filter(Boolean);
      },

      /**
       * Per-cell field error; unknown API keys (no column) surface on the first cell.
       */
      checklistCellError(index, field) {
        const fields = this.errorsonly[index] || {};
        let msg = getRowFieldErrorMessage(fields, field);
        const firstField = this.checklistPreviewColumnKeys()[0];
        if (field === firstField) {
          const unmapped = pickUnmappedRowFieldErrorMessages(
            fields,
            this.checklistPreviewColumnKeys(),
          ).join('; ');
          if (unmapped) {
            msg = [msg, unmapped].filter(Boolean).join('; ');
          }
        }
        return msg || '';
      },

      checklistCellClass(index, header) {
        const classes = [];
        if (header.value === 'description') {
          classes.push('bulk-import-dialog__cell--truncate');
        }
        if (this.checklistCellError(index, header.value)) {
          classes.push('bulk-import-dialog__cell--error');
        }
        return classes;
      },
    },
    watch: {
      /**
       * Watch for changes in error filter toggle
       * Filters error display based on upload type structure
       */
      showdatawitherror(newVal) {
        if (!newVal) {
          // Show all data (including rows without errors)
          this.errorsonly = this.dataerror;
          return;
        }
        
        // Filter to show only rows with errors based on upload type
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          // Checklist: flat error object structure
          this.errorsonly = this.dataerror.filter(item => Object.keys(item).length > 0);
        } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          // Trial Balance: nested array error structure
          this.errorsonly = this.dataerror.filter(item =>
            Array.isArray(item) && item.some(innerArray => 
              Array.isArray(innerArray) && innerArray.some(value => value && value.trim() !== '')
            )
          );
        }
      },
      isOpen(open) {
        if (this.isInformationRequestMode && !open) {
          this.resetInformationRequestShell();
        }
      },
      requestUploadData() {
        if (this.isInformationRequestMode) {
          this.irShowDataWithError = false;
        }
      },
    },
    computed: {
      isBulkEditMode() {
        return this.external && this.externalMode === 'bulk-edit';
      },

      isInformationRequestMode() {
        return this.external && this.externalMode === 'information-request';
      },

      isDialog() {
        if (this.isInformationRequestMode) return true;
        return this.container === 'dialog';
      },

      isOpen() {
        if (this.isInformationRequestMode && this.value !== undefined) {
          return this.value;
        }
        return this.external ? this.visible : this.dialog;
      },

      showTrigger() {
        return !this.external && !this.hideButton;
      },

      /** Edit drawer unmounts while closed. Checklist drawer and the import dialog stay mounted. */
      showContainer() {
        if (this.isDialog) return true;
        if (this.external) return this.isOpen;
        return true;
      },

      chromeComponent() {
        return this.isDialog ? 'BulkUploadDialog' : 'lb-drawer';
      },

      chromeBindings() {
        if (this.isDialog) {
          return {
            value: this.isOpen,
            heading: this.resolvedHeading,
            testId: this.resolvedTestIds.dialog,
            closeTestId: this.resolvedTestIds.close,
          };
        }
        return {
          value: this.isOpen,
          Maxwidth: this.resolvedMaxWidth,
          heading: this.resolvedHeading,
          loading: this.resolvedSurfaceLoading,
          'data-testid': this.resolvedTestIds.drawer,
        };
      },

      cancelButtonProps() {
        if (this.isDialog) {
          return { class: 'bulk-upload-modal__cancel-btn secondaryBtn' };
        }
        return {
          outlined: true,
          class: 'bulk-import-dialog__btn text-none secondaryBtn',
        };
      },

      uploadButtonProps() {
        if (this.isDialog) {
          return { class: 'bulk-upload-modal__upload-btn primaryBtn ml-0' };
        }
        return {
          color: 'primary',
          class: 'bulk-import-dialog__btn text-none white--text',
        };
      },

      resolvedTestIds() {
        return buildBulkUploadTestIds(this.testIdPrefix, this.testIds);
      },

      resolvedHeading() {
        if (this.heading) return this.heading;
        if (this.isBulkEditMode) return 'Bulk Tasklist Edit';
        if (this.isInformationRequestMode) return 'Import request';
        return this.drawerHeading;
      },

      resolvedMaxWidth() {
        if (this.maxWidth) return this.maxWidth;
        return this.resolvedHasPreview ? this.drawerWidths.expanded : this.drawerWidths.lg;
      },

      resolvedSurfaceLoading() {
        if (this.isBulkEditMode) return this.busy && this.bulkEditHasUploadPreview;
        if (this.external) return this.busy && this.previewReady;
        return this.loading && this.hasUploadPreview;
      },

      resolvedSubmitLoading() {
        if (!this.external) return this.loading && this.hasUploadPreview;
        if (this.isDialog) return this.busy;
        return this.busy && this.resolvedHasPreview;
      },

      resolvedHasPreview() {
        if (this.isBulkEditMode) return this.bulkEditHasUploadPreview;
        if (this.isInformationRequestMode) return this.requestHasPreview;
        return this.external ? this.previewReady : this.hasUploadPreview;
      },

      resolvedStep1() {
        if (this.step1Text) return this.step1Text;
        if (this.isBulkEditMode) {
          return 'Start by downloading the bulk edit Excel template for the selected tasks.';
        }
        if (this.isInformationRequestMode) {
          return 'Start by downloading the standard Excel template containing your information request data.';
        }
        return this.step1Description;
      },

      resolvedStep2Title() {
        if (this.resolvedHasPreview) return 'Preview';
        if (this.step2Heading) return this.step2Heading;
        if (this.isInformationRequestMode) return 'Step 2: Upload Updated File';
        return this.step2Title;
      },

      resolvedStep2() {
        if (this.step2Text) return this.step2Text;
        if (this.isBulkEditMode) {
          return 'Fill in the Excel file, then drag and drop it here to update the selected tasks.';
        }
        if (this.isInformationRequestMode) {
          return 'Save your changes in the Excel file, then drag and drop it here to update your requests in the system.';
        }
        return this.step2Description;
      },

      resolvedValidating() {
        if (this.isBulkEditMode) return this.busy && !this.bulkEditHasUploadPreview;
        if (this.isInformationRequestMode) return this.validating;
        return this.external ? this.validating : this.parsing;
      },

      resolvedSelectedFileName() {
        if (this.isBulkEditMode) {
          const current = Array.isArray(this.incomingFile) ? this.incomingFile[0] : this.incomingFile;
          return current?.name || this.selectedFileName || this.fileName || '';
        }
        if (this.isInformationRequestMode) {
          const current = Array.isArray(this.irFiles) ? this.irFiles[0] : this.irFiles;
          return current?.name || this.selectedFileName || this.fileName || '';
        }
        return this.external ? this.fileName : this.selectedFileName;
      },

      resolvedShowReupload() {
        if (this.isBulkEditMode) {
          return this.bulkEditHasUploadPreview || (this.busy && !this.bulkEditHasUploadPreview);
        }
        if (this.isInformationRequestMode) {
          return this.requestHasPreview || this.validating;
        }
        if (this.external) return this.reuploadVisible;
        return this.hasUploadPreview || this.parsing;
      },

      resolvedFile() {
        if (this.isInformationRequestMode) return this.irFiles;
        return this.external ? this.incomingFile : this.file;
      },

      resolvedResetTrigger() {
        if (this.isInformationRequestMode) return this.irResetTrigger;
        return this.external ? this.resetDropzone : this.resetTrigger;
      },

      resolvedAccept() {
        if (this.accept) return this.accept;
        if (this.isBulkEditMode) return '.xlsx,.xls,.xlsm,.xlsb';
        if (this.isInformationRequestMode) return '.xlsx,.xls,.xlsm,.xlsb,.csv';
        return this.fileAccept;
      },

      checklistNote() {
        if (this.uploadType !== BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) return '';
        return '(Note: Preparer & Approver cannot be the same user)';
      },

      resolvedShowInlineErrors() {
        return !this.external && this.uploadBlockingErrors;
      },

      resolvedInlineNote() {
        if (this.external || !this.uploadBlockingErrors) return '';
        return this.checklistNote;
      },

      resolvedHeaderErrors() {
        return this.external ? [] : this.headerErrorMessages;
      },

      resolvedRowErrors() {
        return this.external ? [] : this.errorsonly;
      },

      resolvedFooterVisible() {
        if (this.isBulkEditMode) {
          return this.bulkEditShowErrorFilterToggle
            || this.hasFileDataError
            || this.hasValidationErrors
            || this.bulkEditFooterErrorMessages.length > 0;
        }
        if (this.isInformationRequestMode) {
          return this.requestShowErrorFilterToggle || this.errorFlag;
        }
        if (this.external) return this.footerVisible;
        return this.showBulkErrorFooter || this.showErrorFilterToggle;
      },

      resolvedErrorFooterVisible() {
        if (this.isBulkEditMode) return this.hasFileDataError || this.hasValidationErrors;
        if (this.isInformationRequestMode) return this.errorFlag;
        if (this.external) return this.errorFooterVisible;
        return this.showBulkErrorFooter;
      },

      resolvedErrorFooterText() {
        if (this.errorFooterText) return this.errorFooterText;
        if (this.isInformationRequestMode) {
          return 'We found a few errors. Rectify these errors to proceed to upload.';
        }
        return this.bulkUploadErrorFooterText;
      },

      resolvedErrorFooterNote() {
        if (this.isBulkEditMode) {
          return this.errorFooterNote || '(Preparer & Approver cannot be the same user)';
        }
        if (this.external) return this.errorFooterNote;
        if (!this.showBulkErrorFooter) return '';
        return this.checklistNote;
      },

      resolvedSummaryErrors() {
        if (this.isBulkEditMode) return this.bulkEditFooterErrorMessages;
        if (this.isInformationRequestMode) return [];
        if (this.external) return this.footerSummary || [];
        return this.summaryErrorMessages;
      },

      resolvedErrorFilterVisible() {
        if (this.isBulkEditMode) return this.bulkEditShowErrorFilterToggle;
        if (this.isInformationRequestMode) return this.requestShowErrorFilterToggle;
        if (this.external) return this.errorFilterVisible;
        return this.showErrorFilterToggle;
      },

      resolvedShowErrorsOnly() {
        if (this.isInformationRequestMode) return this.irShowDataWithError;
        return this.external ? this.showErrorsOnly : this.showdatawitherror;
      },

      resolvedCanSubmit() {
        if (this.isBulkEditMode) {
          return this.bulkEditHasUploadPreview && !this.hasFileDataError && !this.hasValidationErrors;
        }
        if (this.isInformationRequestMode) {
          return this.requestHasPreview && !this.errorFlag && !this.validating;
        }
        return this.external ? this.submitEnabled : this.canSubmitUpload;
      },

      frameProps() {
        return {
          testIds: this.resolvedTestIds,
          step1Description: this.resolvedStep1,
          step2Title: this.resolvedStep2Title,
          step2Description: this.resolvedStep2,
          hasPreview: this.resolvedHasPreview,
          validating: this.resolvedValidating,
          selectedFileName: this.resolvedSelectedFileName,
          showSelectedFileName: this.external ? this.showSelectedFileName : true,
          showReupload: this.resolvedShowReupload,
          file: this.resolvedFile,
          resetTrigger: this.resolvedResetTrigger,
          fileAccept: this.resolvedAccept,
          hideDropzoneDetails: this.isInformationRequestMode
            ? false
            : (this.external ? this.hideDropzoneDetails : true),
          singleFile: this.isInformationRequestMode
            ? true
            : (this.external ? this.singleFile : false),
          dropzoneAccept: this.isInformationRequestMode
            ? (this.dropzoneAccept || '.xlsx,.xls,.xlsm,.xlsb,.csv')
            : this.dropzoneAccept,
          showInlineErrors: this.resolvedShowInlineErrors,
          inlineErrorText: this.bulkUploadErrorFooterText,
          inlineErrorNote: this.resolvedInlineNote,
          headerErrorMessages: this.resolvedHeaderErrors,
          rowErrors: this.resolvedRowErrors,
          showPreviewFooter: this.resolvedFooterVisible,
          showErrorFooter: this.resolvedErrorFooterVisible,
          errorFooterText: this.resolvedErrorFooterText,
          errorFooterNote: this.resolvedErrorFooterNote,
          summaryErrorMessages: this.resolvedSummaryErrors,
          summaryTight: this.isBulkEditMode ? true : this.summaryTight,
          showErrorFilter: this.resolvedErrorFilterVisible,
          showErrorsOnly: this.resolvedShowErrorsOnly,
        };
      },

      bulkEditHasUploadPreview() {
        return (this.fileDataList || []).length > 0;
      },

      bulkEditShowErrorFilterToggle() {
        return this.hasFileDataError || this.showErrorsOnly;
      },

      /**
       * Only `_row` summary messages. Every other field key stays on the preview row.
       */
      bulkEditFooterErrorMessages() {
        const fromProp = Array.isArray(this.errorResponse) ? this.errorResponse.filter(Boolean) : [];
        if (fromProp.length) return fromProp;
        const messages = [];
        (this.rowErrors || []).forEach((fields, index) => {
          const msgs = pickNonRowFieldErrorMessages(fields || {});
          if (msgs.length) {
            messages.push(`Row ${index + 1}: ${msgs.join('; ')}`);
          }
        });
        return messages;
      },

      requestHasPreview() {
        return (this.requestUploadData || []).length > 0;
      },

      requestShowErrorFilterToggle() {
        return this.requestHasPreview && (this.errorFlag || this.irShowDataWithError);
      },

      filteredRequestUploadData() {
        if (!this.irShowDataWithError) return this.requestUploadData;
        return (this.requestUploadData || []).filter((row) => this.requestRowHasError(row));
      },

      /**
       * Dynamic drawer heading based on upload type
       */
      drawerHeading() {
        return 'Bulk Upload';
      },

      hasUploadPreview() {
        return this.uploaddata.length > 0;
      },

      step1Description() {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          return 'Start by downloading the Trial Balance Excel template.';
        }
        if (this.project) {
          return 'Start by downloading the checklist template for this project.';
        }
        return 'Start by downloading the standard Excel template for importing task templates.';
      },

      step2Title() {
        return 'Step 2: Upload File';
      },

      step2Description() {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          return 'Fill in the Excel file, then drag and drop it here to import trial balance data.';
        }
        return 'Fill in the Excel file, then drag and drop it here to import your tasks into the system';
      },

      bulkUploadErrorFooterText() {
        return '*These are fields where errors exist / information missing. Please rectify and re-upload to continue.';
      },

      uploadBlockingErrors() {
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          return this.flag && !this.hasUploadPreview;
        }
        return (this.haserror || this.tberrorflag) && !this.hasUploadPreview;
      },

      showBulkErrorFooter() {
        if (!this.hasUploadPreview) return false;
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) return this.flag;
        return this.haserror || this.tberrorflag;
      },

      showErrorFilterToggle() {
        return this.hasUploadPreview && (this.showBulkErrorFooter || this.showdatawitherror);
      },

      /**
       * Footer list: `_row` summary only — every other fields key stays on the preview row.
       */
      summaryErrorMessages() {
        if (!this.hasUploadPreview) return [];
        const messages = [];
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
          (this.dataerror || []).forEach((rowErrors, index) => {
            const msgs = pickNonRowFieldErrorMessages(rowErrors || {});
            if (msgs.length) {
              messages.push(`Row ${index + 1}: ${msgs.join('; ')}`);
            }
          });
          return messages;
        }
        // Trial balance: index 0 holds `_row`-style messages not mapped to a column.
        (this.dataerror || []).forEach((groupErrors, index) => {
          const rowMsg = groupErrors?.[0]?.[0];
          if (rowMsg && String(rowMsg).trim()) {
            messages.push(`Row ${index + 1}: ${String(rowMsg).trim()}`);
          }
        });
        return messages;
      },

      canSubmitUpload() {
        if (!this.hasUploadPreview) return false;
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) return !this.flag;
        return !this.tberrorflag;
      },

      previewTableHeaders() {
        return this.computedHeaders;
      },

      fileAccept() {
        return '.xlsx,.xls,.xlsm,.xlsb,.csv';
      },

      /**
       * Schema-defined custom columns only — excludes task-data keys (manager, customfields, etc.)
       * that are not part of the bulk import Excel template.
       */
      importCustomColumns() {
        const schema = this.$store.getters['tasks/getTaskListData']?.customColumnSchema
          || this.$store.getters['tasks/getFormDataV3']?.clientCustomColumns
          || [];
        if (Array.isArray(schema) && schema.length) {
          return getCustomColumns({}, schema).filter((col) => {
            if (!col?.columnName) return false;
            const entry = schema.find((s) => s.columnName === col.columnName);
            return !entry?.isSystem;
          });
        }
        return (this.customColumns || []).filter(
          (col) => col?.columnName && !IMPORT_BLOCKED_CUSTOM_KEYS.has(col.columnName)
        );
      },
      
      /**
       * Computed table headers based on upload type
       * Returns different column configurations for checklist vs trial balance
       */
      computedHeaders() {
        // Use custom headers if provided
        if (this.tableHeaders && this.tableHeaders.length > 0) {
          return this.tableHeaders;
        }
        
        // Trial Balance Headers
        if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
          return [
            { text: 'Account No.', value: 'glcode', align: 'left', errorIndex: 1 },
            { text: 'Opening Amount', value: 'opening_amount', align: 'right', format: 'number' },
            { text: 'Transaction', value: 'transaction', align: 'right', format: 'number' },
            { text: 'Journal Entries', value: 'audit_entries', align: 'right', format: 'number' },
            { text: 'Closing Amount', value: 'closing_amount', align: 'right', format: 'number' },
            { text: this.$store.state.LocationHeader || 'Location', value: 'location', align: 'left', errorIndex: 5 }
          ];
        } else {
          // Checklist Headers
          const baseHeaders = [
            { text: 'Task Type', value: 'task_type' },
            { text: 'Task Name', value: 'description' },
            { text: 'Category', value: 'category' },
            { text: 'Sub Category', value: 'subcategory' },
            { text: 'Location', value: 'location' },
            { text: 'Assertions', value: 'assertions' },
            { text: 'Purpose', value: 'purpose' },
            { text: 'Instruction', value: 'instruction' },
            { text: 'Preparer', value: 'preparer' },
            { text: 'Approver', value: 'approver' },
            { text: SIGN_OFF.APPROVER_LABEL, value: 'sign_off_approver' },
            { text: 'Frequency', value: 'tags' }
          ];
          
          baseHeaders.push(
            { text: 'Due days', value: 'duedays' },
            { text: 'Account No.', value: 'glcode' },
            { text: 'Priority', value: 'priority' },
            { text: 'No. of Evidences', value: 'no_of_evidence' },
            { text: 'Evidence Required', value: 'evidence_required' },
            { text: 'Enforce Threshold', value: 'enforceThreshold' },
            { text: AUTO_APPROVE.EXCEL_HEADER, value: 'auto_approve' }
          );
          
          if (this.importCustomColumns && this.importCustomColumns.length > 0) {
            this.importCustomColumns.forEach((col) => {
              // Add date suffix for date-type custom columns
              const headerText = col.dataType === 'date' 
                ? `${col.displayText} (dd/mm/yyyy)` 
                : col.displayText;
              baseHeaders.push({ text: headerText, value: col.columnName });
            });
          }
          
          return baseHeaders;
        }
      },
      
      /**
       * Filtered data for display based on error filter toggle
       * Handles different data structures for checklist vs trial balance
       */
      filteredData() {
        if (this.showdatawitherror) {
          // Filter by upload type
          if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.CHECKLIST) {
            return this.uploaddata.filter((_, rowIndex) =>
              Object.values(this.dataerror[rowIndex] || {}).some((error) => error)
            );
          } else if (this.uploadType === BULK_CHECKLIST_UPLOAD_TYPE.TRIAL_BALANCE) {
            return this.uploaddata.filter((_, index) => {
              const hasError = this.dataerror[index]?.some(errors =>
                errors.some(msg => msg && msg.trim() !== '')
              );
              return hasError;
            });
          }
        }
        return this.uploaddata;
      }
    }
  }
  </script>

<style lang="scss" scoped>
@import './BulkUpload.scss';
</style>
